import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook that provides RAF (requestAnimationFrame) throttling for callbacks.
 * Useful for throttling rapid events like arrow key presses to once per frame.
 *
 * @returns A throttle function that wraps callbacks in RAF throttling
 *
 * @example
 * const throttle = useRafThrottle();
 *
 * const handleKeyDown = (key: string) => {
 *   if (key === 'ArrowUp') {
 *     throttle(() => moveFocusUp());
 *   }
 * };
 */
export const useRafThrottle = () => {
  const rafIdRef = useRef<number | null>(null);

  // Clean up RAF on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const throttle = useCallback((callback: () => void) => {
    if (rafIdRef.current !== null) {
      return;
    }
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      callback();
    });
  }, []);

  return throttle;
};
